import { useCallback, useEffect, useState } from 'react';
import { Package, Pencil, Trash2 } from 'lucide-react';
import api from '@/shared/api/client';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import { toast } from 'sonner';

/**
 * Les produits ajoutés à la main par le SAV, sous la liste du parc enregistré.
 *
 * Une section à part, et pas des lignes mêlées au parc : ces produits n'ont ni chantier
 * garanti, ni connexion, ni fiche chantier à ouvrir. Les mêler laisserait croire qu'un
 * clic ouvre la même chose. Les MÊMES filtres s'y appliquent (recherche, type), pour
 * qu'une recherche ne réponde pas « rien » alors que la fiche existe ici.
 *
 * `version` est incrémenté par la page après un ajout : il suffit à recharger.
 */
export default function ProduitsAjoutesSav({ recherche, type, version, onModifier }) {
  const [produits, setProduits] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const params = {};
      if (recherche) params.search = recherche;
      if (type && type !== 'all') params.type = type;
      const r = await api.get('/distributeur/produits', { params });
      setProduits(r.data || []);
    } catch {
      setProduits([]);
    }
    setChargement(false);
  }, [recherche, type]);

  useEffect(() => { charger(); }, [charger, version]);

  const supprimer = async () => {
    try {
      await api.delete(`/distributeur/produits/${aSupprimer.id}`);
      toast.success('Produit supprimé');
      setASupprimer(null);
      charger();
    } catch {
      toast.error("Le produit n'a pas été supprimé");
    }
  };

  if (chargement && produits.length === 0) return null;

  return (
    <Card data-testid="produits-ajoutes-sav">
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
          <Package className="w-4 h-4 text-neutral-500" />
          <h2 className="text-[13px] font-medium text-neutral-900">Produits ajoutés par le SAV</h2>
          <span className="text-[11px] text-neutral-400">({produits.length})</span>
        </div>
        {produits.length === 0 ? (
          <p className="text-sm text-neutral-400 py-6 text-center" data-testid="produits-ajoutes-vide">
            Aucun produit ajouté{recherche || (type && type !== 'all') ? ' pour ces filtres' : ''}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 border-b border-neutral-100">
                <tr className="text-left text-neutral-500 text-[11px] uppercase tracking-wide">
                  <th className="py-2.5 px-4 font-medium">Type</th>
                  <th className="py-2.5 px-4 font-medium">Marque / Modèle</th>
                  <th className="py-2.5 px-4 font-medium">N° série</th>
                  <th className="py-2.5 px-4 font-medium">Puissance</th>
                  <th className="py-2.5 px-4 font-medium">Date pose</th>
                  <th className="py-2.5 px-4 font-medium">Réf. chantier</th>
                  <th className="py-2.5 px-4 font-medium">Ajouté par</th>
                  <th className="py-2.5 px-4" />
                </tr>
              </thead>
              <tbody>
                {produits.map(p => (
                  <tr key={p.id} className="border-b border-neutral-100" data-testid={`produit-row-${p.id}`}>
                    <td className="py-3 px-4">{p.type}</td>
                    <td className="py-3 px-4">{[p.marque, p.modele].filter(Boolean).join(' · ') || '-'}</td>
                    <td className="py-3 px-4 font-mono text-[12px]">{p.numero_serie || '-'}</td>
                    <td className="py-3 px-4">{p.puissance_kw != null ? `${p.puissance_kw} kW` : '-'}</td>
                    <td className="py-3 px-4">{p.date_installation || '-'}</td>
                    <td className="py-3 px-4">{p.reference_chantier || '-'}</td>
                    <td className="py-3 px-4 text-neutral-500">{p.created_by_nom || '-'}</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onModifier(p)}
                              aria-label="Modifier" data-testid={`produit-modifier-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={() => setASupprimer(p)}
                              aria-label="Supprimer" data-testid={`produit-supprimer-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        pending={aSupprimer && {
          title: 'Supprimer ce produit', target: `${aSupprimer.type} ${aSupprimer.numero_serie || ''}`.trim(),
          targetLabel: 'Produit', warning: 'La fiche sera supprimée définitivement.',
          tone: 'danger', confirmLabel: 'Supprimer',
        }}
        onCancel={() => setASupprimer(null)}
        onConfirm={supprimer}
      />
    </Card>
  );
}
